import "./CSIAssign.css";
import React, {useCallback, useRef, useState} from "react";
import {DataLoadingStatus} from "../../../Utils/enums";
import {
    checkAssignStatus,
    deleteHistoryFile,
    exportResults,
    getDivisions,
    getHistory,
    uploadCompressedCsv
} from "../../../Utils/queries";
import {Division, UserFile} from "../../../Utils/response-types";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faDownload, faTrash} from "@fortawesome/free-solid-svg-icons";

const CSIAssign = ({is_ai}: {is_ai: boolean}) => {
    const [history, setHistory] = useState<UserFile[]>([]);
    const [divisions, setDivisions] = useState<Division[]>([]);
    const [selectedDivisionId, setSelectedDivisionId] = useState<number>(0);
    const [hStatus, setHStatus] = useState<DataLoadingStatus>(DataLoadingStatus.NotLoaded);
    const [dStatus, setDStatus] = useState<DataLoadingStatus>(DataLoadingStatus.NotLoaded);
    const [fileLoadingStatus, setFileLoadingStatus] = useState<DataLoadingStatus>(DataLoadingStatus.NotLoaded);
    const [file, setFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [uniqueId, setUniqueId] = useState<string|undefined>(undefined);

    if (hStatus === DataLoadingStatus.NotLoaded) {
        setHStatus(DataLoadingStatus.Loading);
        getHistory(is_ai).then(resp => {
            if (resp.errorCode === undefined) {
                setHistory(resp.history.sort((r1, r2) => r2.creation_date.localeCompare(r1.creation_date)));
                setHStatus(DataLoadingStatus.Loaded);
            } else {
                setHStatus(DataLoadingStatus.Error);
            }
        });
    }

    if (!is_ai && dStatus === DataLoadingStatus.NotLoaded && hStatus === DataLoadingStatus.NotLoaded) {
        setDStatus(DataLoadingStatus.Loading);
        getDivisions().then(resp => {
            if (resp.errorCode === undefined) {
                setDivisions(resp.divisions);
                setDStatus(DataLoadingStatus.Loaded);
            } else {
                setDStatus(DataLoadingStatus.Error);
            }
        });
    }

    const handleFileSelect = (selectedFile: File | null) => {
        setError(null);
        setFileLoadingStatus(DataLoadingStatus.NotLoaded);
        if (selectedFile && (selectedFile.name.toLowerCase().endsWith('.csv') || selectedFile.name.toLowerCase().endsWith('.xlsx') || selectedFile.name.toLowerCase().endsWith('.xls') || selectedFile.name.toLowerCase().endsWith('.parquet'))) {
            setFile(selectedFile);
        } else {
            setFile(null);
            if (selectedFile) {
                setError('Please select a .csv, .xlsx or .parquet file.');
                setFileLoadingStatus(DataLoadingStatus.Error);
            }
        }
    };

    const scheduleUpdateStatus = useCallback((delay: number, unique_id: string) => {
        setTimeout(() => {
            checkAssignStatus(unique_id).then(resp => {
            if (resp.errorCode === undefined) {
                if (resp.success) {
                    exportResults(unique_id).then(res => {
                        if (res){
                            setFileLoadingStatus(DataLoadingStatus.Loaded);
                            setFile(null);
                            setHStatus(DataLoadingStatus.NotLoaded);
                        } else {
                            setError("Error downloading the results!")
                            setFileLoadingStatus(DataLoadingStatus.Error);
                        }
                    })
                } else {
                    scheduleUpdateStatus(Math.min(delay*1.2, 20000), unique_id);
                }
            } else {
                setError(`Error fetching the status: ${resp.errorCode}`)
                setFileLoadingStatus(DataLoadingStatus.Error);
            }
        });
        }, delay);
    }, [setFileLoadingStatus, setHStatus, setError, setFile]);

    const handleUpload = useCallback(async () => {
        if (!file || fileLoadingStatus === DataLoadingStatus.Loading || fileLoadingStatus === DataLoadingStatus.IdleLoading) return;
        setFileLoadingStatus(DataLoadingStatus.Loading);
        setError(null);
        try {
            const response = await uploadCompressedCsv(is_ai, file, selectedDivisionId);

            if (response.success) {
                setUniqueId(response.unique_id);
                setFileLoadingStatus(DataLoadingStatus.IdleLoading);
                scheduleUpdateStatus(3000, response.unique_id);
            } else {
                const errorMsg = `Upload failed: ${response.message}${response.errorCode ? ` (Code: ${response.errorCode})` : ''}`;
                setError(errorMsg);
                setFileLoadingStatus(DataLoadingStatus.Error);
            }
        } catch (err: any) {
            console.error("Upload process failed:", err);
            setError(err.message || 'Upload failed due to an unexpected error.');
            setFileLoadingStatus(DataLoadingStatus.Error);
        }
    }, [file, selectedDivisionId, fileLoadingStatus, setFileLoadingStatus, setError, setUniqueId, scheduleUpdateStatus, is_ai]);
    
    return (
        <div className="main-content">
            <nav className="history-nav">
              <h2 className="history-heading">History:</h2>
                { (hStatus !== DataLoadingStatus.Loaded || !history.length) &&
                    <p id="h-status">{
                        hStatus === DataLoadingStatus.Loading? "Fetching..." :
                            hStatus === DataLoadingStatus.Error? "Error loading history" :
                                !history.length? "Nothing here..." : ""
                    }</p>
                }
                <div id="history-container">{
                  history.map(h => <HistoryRow key={h.filename} row={h} setHistory={setHistory} setStatus={setHStatus} />)
              }</div>
            </nav>
            <div className="csi-main">
                <div className="uploader-container">
                    <h2>Assign CSI {is_ai ? "with AI" : "by keywords"}</h2>
                    <p className="description">Upload your {is_ai ? "general" : "full"} CSV file to categorize CSI
                        products {is_ai ? "with AI. Only the unique name+URL+Shape combinations will be used" : "by keywords"}.</p>

                    { !is_ai && (divisions.length > 0) && <select value={selectedDivisionId} className="division-select" onChange={(e) => setSelectedDivisionId(Number(e.target.value))}>
                        <option value="0">-- select a division ---</option>
                        { divisions.map((division) => (
                            <option key={division.division_id} value={division.division_id}>
                                {division.division_name}
                            </option>
                        )) }
                      </select>
                    }

                    <div id="drop-area"
                         onClick={() => fileLoadingStatus !== DataLoadingStatus.Loading && inputRef.current?.click()}
                         onDrop={(e) => {
                             e.preventDefault();
                             fileLoadingStatus !== DataLoadingStatus.Loading && handleFileSelect(e.dataTransfer.files?.[0] ?? null);
                         }}
                         onDragOver={(e) => e.preventDefault()}>
                        <p id="drop-area-description">{file ? file.name : "Drag & drop CSV file here, or click to select"}</p>
                        <input ref={inputRef} type="file" id="csvFile" accept=".csv" hidden
                               onChange={(e) => {
                                   handleFileSelect(e.target.files?.[0] ?? null);
                                   if (e.target) e.target.value = '';
                               }}
                               disabled={fileLoadingStatus === DataLoadingStatus.Loading}/>
                    </div>
                    <br/>
                    <button id="uploadBtn" className="upload-btn"
                            disabled={!file || (!is_ai && !selectedDivisionId) || fileLoadingStatus === DataLoadingStatus.Loading || fileLoadingStatus === DataLoadingStatus.IdleLoading}
                            onClick={handleUpload}>Upload
                    </button>
                    { fileLoadingStatus !== DataLoadingStatus.NotLoaded && <p id="status">{
                        fileLoadingStatus === DataLoadingStatus.Loading ? "Uploading file..." :
                            fileLoadingStatus === DataLoadingStatus.IdleLoading ? "File uploaded. ID: " + uniqueId + ". Processing..." :
                                fileLoadingStatus === DataLoadingStatus.Loaded ? "File successfully downloaded!" :
                                    fileLoadingStatus === DataLoadingStatus.Error ? "Error: " + error : ""
                    }</p>}
                </div>
            </div>
        </div>
    );
};

const HistoryRow = ({row, setHistory, setStatus}: {
    row: UserFile,
    setHistory: React.Dispatch<React.SetStateAction<UserFile[]>>,
    setStatus: React.Dispatch<React.SetStateAction<DataLoadingStatus>>
}) => {

    const deleteHistoryEntry = useCallback(async () => {
        // eslint-disable-next-line no-restricted-globals
        const confirmed = confirm("Do you really want to delete this file from history? This action cannot be undone!");
        if (confirmed) {
            setStatus(DataLoadingStatus.Loading);
            deleteHistoryFile(row.filename).then(resp => {
                if (resp.errorCode !== undefined) {
                    alert(`Error: ${resp.errorCode}`);
                    setStatus(DataLoadingStatus.Error);
                } else {
                    alert("History entry deleted successfully!");
                    setHistory(resp.history!);
                    setStatus(DataLoadingStatus.Loaded);
                }
            });
        }
    }, [row, setHistory, setStatus]);

    const downloadRes = useCallback(async ()=> {
        setStatus(DataLoadingStatus.Loading);
        exportResults(row.filename).then(resp => {
            setStatus(resp? DataLoadingStatus.Loaded : DataLoadingStatus.Error);
        });
    }, [row, setStatus]);

    return (
        <div className="history-entry">
            <div className="history-data-container">
                <p className="history-file-name">{row.filename}</p>
                <p className="history-creation-date">{row.creation_date.split('.')[0].replace('T', ' ')}</p>
            </div>
            <button className="history-action-button" aria-label="Download Result" title="Download Result" onClick={downloadRes}><FontAwesomeIcon icon={faDownload}/></button>
            <button className="history-action-button" aria-label="Delete" title="Delete" onClick={deleteHistoryEntry}><FontAwesomeIcon icon={faTrash}/></button>
        </div>
    );
}

export default CSIAssign;